/**
 * Шкала времени для MapTimeline.
 *
 * Преобразует «доменное» значение слоя (час, месяц, год, дата, момент смены)
 * в числовое пространство, понятное rc-slider, и обратно. Компонент знает
 * только про числа min/max/step; вся доменная семантика инкапсулирована здесь.
 *
 * Поддерживаются два типа домена:
 *  - categorical — дискретный набор значений (часы 0–23, месяцы, годы).
 *    Числовое пространство = индексы 0..n-1, step = 1.
 *  - continuous  — непрерывный числовой диапазон (epoch-ms даты, [shift_start,
 *    shift_end] смены). Числовое пространство = сами значения домена.
 */

const DEFAULT_FORMAT = v => String(v);
/**
 * Нормализует элемент categorical-домена к { value, label }.
 * Допускает примитив (value === label) или объект { value, label }.
 */
const normalizeItem = (item, format) => {
    if (item !== null && typeof item === 'object' && 'value' in item) {
        return { value: item.value, label: item.label ?? format(item.value) };
    }
    return { value: item, label: format(item) };
};
/**
 * Прореживает массив тиков до не более чем maxTicks штук, сохраняя первый и
 * последний. Нужно, чтобы подписи под рельсой не сливались (24 часа, годы — ок;
 * сотни дат — нет).
 */
const thinTicks = (ticks, maxTicks) => {
    if (!maxTicks || ticks.length <= maxTicks) return ticks;
    const stepSize = Math.ceil((ticks.length - 1) / (maxTicks - 1));
    const result = [];
    for (let i = 0; i < ticks.length; i += stepSize) result.push(ticks[i]);
    const last = ticks[ticks.length - 1];
    if (result[result.length - 1] !== last) result.push(last);
    return result;
};

/**
 * Строит шкалу по описанию домена.
 *
 * @param {Object} domain
 * @param {'categorical'|'continuous'} domain.type
 * @param {Array}  [domain.values]  — для categorical: список значений/объектов
 * @param {[number,number]} [domain.values] — для continuous: [min, max]
 * @param {Function} [domain.format] — форматтер доменного значения в подпись
 * @param {number} [domain.step]     — для continuous: шаг бегунка (по умолчанию непрерывный)
 * @param {Array}  [domain.ticks]    — для continuous: явные засечки [{ value, label }] вместо
 *                                      равномерной авто-сетки (используются как есть, без прореживания)
 * @param {number} [maxTicks]        — максимум подписей под рельсой
 * @returns {Object} scale
 */
export const buildTimeScale = (domain, maxTicks = 8) => {
    const format = domain.format || DEFAULT_FORMAT;

    if (domain.type === 'categorical') {
        const items = (domain.values || []).map(it => normalizeItem(it, format));
        const indexByValue = new Map(items.map((it, i) => [ it.value, i ]));
        const allTicks = items.map((it, i) => ({ pos: i, label: it.label }));

        return {
            type: 'categorical',
            min: 0,
            max: Math.max(0, items.length - 1),
            step: 1,
            ticks: thinTicks(allTicks, maxTicks),
            // доменное значение -> позиция бегунка
            toSlider: value => indexByValue.get(value) ?? 0,
            // позиция бегунка -> доменное значение
            fromSlider: pos => {
                const i = Math.round(pos);
                return items[Math.min(Math.max(i, 0), items.length - 1)]?.value;
            },
            format: value => format(value),
        };
    }

    // continuous
    const [ min, max ] = domain.values || [ 0, 0 ];
    const step = domain.step ?? ((max - min) / 1000 || 1);
    // Явные засечки (domain.ticks: [{ value, label }]) — используются как есть, без
    // прореживания (напр. начала месяцев, неравномерные по числу дней). Иначе —
    // равномерная авто-сетка из maxTicks делений.
    let ticks;
    if (Array.isArray(domain.ticks) && domain.ticks.length) {
        ticks = domain.ticks.map(tk => ({ pos: tk.value, label: tk.label ?? format(tk.value) }));
    } else {
        ticks = [];
        const tickCount = Math.min(maxTicks, 8);
        for (let i = 0; i < tickCount; i++) {
            const pos = tickCount === 1 ? min : min + ((max - min) * i) / (tickCount - 1);
            ticks.push({ pos, label: format(pos) });
        }
    }

    return {
        type: 'continuous',
        min,
        max,
        step,
        ticks,
        toSlider: value => value,
        fromSlider: pos => pos,
        format: value => format(value),
    };
};
